import { useMemo } from 'react';

import {
  getDeviceLightCapabilityIdList,
  getDeviceLightCapabilityIds,
  getDeviceLightCapabilityValues,
} from '../../lib/capabilities/deviceLightCapabilities';
import {
  getColorForHueSaturation,
  getColorForTemperature,
  getDefaultColor,
  getMode,
} from '../../lib/capabilities/light';
import { round } from '../../lib/math';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useCapabilityValues } from '../../store/devices/useCapabilityValues';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';

import styles from './IndicatorLight.module.css';

import { getModeAdjustedRGB } from './DeviceQuickActionColors';

const LIGHT_INDICATOR_BORDER_OPACITY = 0.3;

export function IndicatorLight(props) {
  const { i18n } = useI18n();
  const { settings } = useWebAppSettings();
  const offLabel = i18n.messageFormatter('common.off');
  const onLabel = i18n.messageFormatter('common.on');
  const isLightTheme = settings.theme.darkMode !== true;
  const lightCapabilityIds = useMemo(() => {
    return getDeviceLightCapabilityIds({ device: props.device });
  }, [props.device]);
  const capabilityIds = useMemo(() => {
    return getDeviceLightCapabilityIdList(lightCapabilityIds);
  }, [lightCapabilityIds]);
  const [capabilityValues] = useCapabilityValues({
    deviceId: props.device.id,
    capabilityIds,
  });

  const indicatorState = useMemo(() => {
    const values = getDeviceLightCapabilityValues({
      capabilityIds: lightCapabilityIds,
      values: capabilityValues,
    });
    const hasDimCapability = lightCapabilityIds.dim != null;
    const hasOnOffCapability = lightCapabilityIds.onoff != null;

    let isOn = true;
    let hasKnownState = true;

    if (hasOnOffCapability === true) {
      if (typeof values.onoff === 'boolean') {
        isOn = values.onoff;
      } else {
        hasKnownState = false;
      }
    } else if (hasDimCapability === true) {
      if (typeof values.dim === 'number') {
        isOn = values.dim > 0;
      } else {
        hasKnownState = false;
      }
    }

    const hasDimValue = typeof values.dim === 'number';
    const normalizedDimValue = typeof values.dim === 'number' ? values.dim : 1;
    const scaledDim = logScale(normalizedDimValue);
    const lightMode = getMode(values);
    const lightColor = getLightColor({ lightMode, values });

    let color = null;
    let borderColor = null;
    let contrastBorderColor = null;

    if (lightColor != null) {
      const rgb = lightColor.toRgb();
      const borderColors = getLightIndicatorBorderColors({
        blue: rgb.b,
        green: rgb.g,
        isLightTheme,
        red: rgb.r,
      });

      color = lightColor.setAlpha(scaledDim).toHslString();
      borderColor = borderColors.borderColor;
      contrastBorderColor = borderColors.contrastBorderColor;
    }

    let label = onLabel;

    if (isOn !== true) {
      label = offLabel;
    } else if (hasDimCapability === true && hasDimValue === true) {
      const percentage = Math.max(0, Math.min(100, Math.round(normalizedDimValue * 100)));
      label = `${onLabel} • ${percentage}%`;
    }

    return {
      color,
      borderColor,
      contrastBorderColor,
      hasKnownState,
      isOn,
      label,
    };
  }, [capabilityValues, isLightTheme, lightCapabilityIds, offLabel, onLabel]);

  const style = {
    '--prop-background-color': indicatorState.color ?? undefined,
    '--prop-border-color': indicatorState.borderColor ?? undefined,
    '--prop-contrast-border-color': indicatorState.contrastBorderColor ?? undefined,
  };

  if (indicatorState.hasKnownState !== true) {
    return null;
  }

  if (indicatorState.isOn !== true) {
    return (
      <div className={styles.root} data-is-light-theme={isLightTheme} style={style}>
        <div className={styles.text}>{indicatorState.label}</div>
      </div>
    );
  }

  return (
    <div className={styles.root} data-is-light-theme={isLightTheme} data-is-on="true" style={style}>
      <div className={styles.icon} />
      <div className={styles.text}>{indicatorState.label}</div>
    </div>
  );
}

function getLightColor({ lightMode, values }) {
  if (
    values.light_temperature == null &&
    values.light_hue == null &&
    values.light_saturation == null
  ) {
    return getDefaultColor();
  }

  if (lightMode === 'color') {
    return getColorForHueSaturation({
      light_hue: values.light_hue,
      light_saturation: values.light_saturation,
    });
  }

  if (lightMode === 'temperature') {
    return getColorForTemperature({
      light_temperature: values.light_temperature,
    });
  }

  return null;
}

function getLightIndicatorBorderColors({ blue, green, isLightTheme, red }) {
  const adjustedColor = getModeAdjustedRGB({ blue, green, isLightTheme, red });

  return {
    borderColor: `rgba(${adjustedColor.red}, ${adjustedColor.green}, ${adjustedColor.blue}, ${LIGHT_INDICATOR_BORDER_OPACITY})`,
    contrastBorderColor: `rgb(${adjustedColor.red}, ${adjustedColor.green}, ${adjustedColor.blue})`,
  };
}

function logScale(dim) {
  const minp = 0;
  const maxp = 1;

  // The result should be between 0.5 and 1
  const minv = Math.log(0.5);
  const maxv = Math.log(1);

  // calculate adjustment factor
  const scale = (maxv - minv) / (maxp - minp);

  return round(Math.exp(minv + scale * (dim - minp)), 2);
}
